import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useExam } from "../context/ExamContext";
import { getPaymentHistory } from "../api/client";
import AppSidebar from "../pages/AppSidebar";

export default function Purchases() {
  const { currentUser, logout } = useExam();
  const navigate = useNavigate();

  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!currentUser) {
      setPurchases([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    getPaymentHistory()
      .then(({ data }) => {
        const rows = Array.isArray(data)
          ? data
          : data?.results || data?.purchases || [];

        setPurchases(rows);
      })
      .catch((err) => {
        console.error("Failed to load payment history:", err);
        setPurchases([]);
        setError("Unable to load your purchase history.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [currentUser]);

  /* ==========================================================
     TOTAL SPENT
     Only successful payments are counted as actual spending.
     ========================================================== */

  const totalSpent = useMemo(
    () =>
      purchases
        .filter(
          (purchase) =>
            String(purchase.status || "").toUpperCase() === "SUCCESS"
        )
        .reduce(
          (sum, purchase) => sum + Number(purchase.amount || 0),
          0
        ),
    [purchases]
  );

  /* ==========================================================
     UPCOMING EXAMS
     Only scheduled purchases with a future/current exam date.
     ========================================================== */

  const upcoming = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return purchases.filter((purchase) => {
      if (!purchase.exam_date) {
        return false;
      }

      const examDate = new Date(`${purchase.exam_date}T00:00:00`);

      return !Number.isNaN(examDate.getTime()) && examDate >= today;
    }).length;
  }, [purchases]);

  /* ==========================================================
     HELPERS
     ========================================================== */

  const formatExamDate = (date) => {
    if (!date) {
      return "Not scheduled";
    }

    const parsedDate = new Date(`${date}T00:00:00`);

    if (Number.isNaN(parsedDate.getTime())) {
      return "Not scheduled";
    }

    return parsedDate.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatAmount = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN");
  };

  const getStatusLabel = (status) => {
    switch (String(status || "").toUpperCase()) {
      case "SUCCESS":
        return "Paid";

      case "PENDING":
        return "Payment Pending";

      case "FAILED":
        return "Failed";

      case "CANCELLED":
        return "Cancelled";

      default:
        return status || "Unknown";
    }
  };

  const getStatusClass = (status) => {
    switch (String(status || "").toUpperCase()) {
      case "SUCCESS":
        return "text-success";

      case "PENDING":
        return "text-warning";

      case "FAILED":
      case "CANCELLED":
        return "text-danger";

      default:
        return "text-muted";
    }
  };

  return (
    <div className="oes-page-full">
      <div className="oes-home-shell d-flex flex-row overflow-hidden">
        <AppSidebar />

        <div className="flex-grow-1 p-3 p-md-4">
          {/* ==================================================
              TOP BAR
              ================================================== */}

          <div className="d-flex justify-content-between align-items-start mb-3">
            <button
              className="btn btn-link btn-sm text-secondary text-decoration-none ps-0"
              onClick={() => navigate("/home")}
            >
              <i className="bi bi-arrow-left me-1" />
              Back to Home
            </button>

            <div className="d-flex align-items-center gap-3">
              <i className="bi bi-bell fs-5 text-secondary" />

              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  logout();
                  navigate("/login");
                }}
              >
                <i className="bi bi-box-arrow-right me-1" />
                Logout
              </button>
            </div>
          </div>

          {/* ==================================================
              PAGE HEADER
              ================================================== */}

          <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 mb-md-4 gap-2">
            <div>
              <h1
                className="h5 fw-bold mb-1"
                style={{ color: "var(--oes-navy)" }}
              >
                Purchase History
              </h1>

              <p className="text-muted small mb-0">
                Every exam you've booked, all in one place.
              </p>
            </div>
          </div>

          {/* ==================================================
              SUMMARY STATS
              ================================================== */}

          <div className="row g-3 mb-3 mb-md-4">
            {/* Total Purchases */}

            <div className="col-6 col-md-4">
              <div className="stat-card">
                <span className="stat-icon">
                  <i className="bi bi-cart3" />
                </span>

                <div className="stat-value">
                  {purchases.length}
                </div>

                <div className="stat-label">
                  Total Purchases
                </div>
              </div>
            </div>

            {/* Upcoming Exams */}

            <div className="col-6 col-md-4">
              <div className="stat-card">
                <span className="stat-icon tone-green">
                  <i className="bi bi-calendar-check" />
                </span>

                <div className="stat-value">
                  {upcoming}
                </div>

                <div className="stat-label">
                  Upcoming Exams
                </div>
              </div>
            </div>

            {/* Total Spent */}

            <div className="col-12 col-md-4">
              <div className="stat-card">
                <span className="stat-icon">
                  <i className="bi bi-wallet2" />
                </span>

                <div className="stat-value">
                  ₹{totalSpent.toLocaleString("en-IN")}
                </div>

                <div className="stat-label">
                  Total Spent
                </div>
              </div>
            </div>
          </div>

          {/* ==================================================
              PURCHASE TABLE
              ================================================== */}

          <div className="oes-card p-3 p-md-4">
            {/* Loading */}

            {loading && (
              <div className="d-flex align-items-center gap-2 text-muted py-4">
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                />

                <span className="small">
                  Loading your purchases…
                </span>
              </div>
            )}

            {/* Error */}

            {!loading && error && (
              <div className="text-center py-5">
                <div
                  className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 14,
                    background: "#fff0f0",
                    color: "#dc3545",
                    fontSize: "1.4rem",
                  }}
                >
                  <i className="bi bi-exclamation-circle" />
                </div>

                <p
                  className="fw-semibold mb-1"
                  style={{ color: "var(--oes-navy)" }}
                >
                  Unable to load purchases
                </p>

                <p className="text-muted small mb-0">
                  {error}
                </p>
              </div>
            )}

            {/* Empty State */}

            {!loading &&
              !error &&
              purchases.length === 0 && (
                <div className="text-center py-5">
                  <div
                    className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 14,
                      background: "#eef1f6",
                      color: "var(--oes-blue)",
                      fontSize: "1.4rem",
                    }}
                  >
                    <i className="bi bi-cart3" />
                  </div>

                  <p
                    className="fw-semibold mb-1"
                    style={{ color: "var(--oes-navy)" }}
                  >
                    No purchases yet
                  </p>

                  <p className="text-muted small mb-0">
                    Book an exam from the Home page to see it listed here.
                  </p>
                </div>
              )}

            {/* Purchase Table */}

            {!loading &&
              !error &&
              purchases.length > 0 && (
                <div className="table-responsive">
                  <table className="table scoreboard-table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Course</th>
                        <th>Exam</th>
                        <th>Exam Date</th>
                        <th>Status</th>
                        <th className="text-end">
                          Fee
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {purchases.map((purchase) => (
                        <tr key={purchase.id}>
                          {/* Course */}

                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <span
                                className="d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: 9,
                                  background: "#e7f0ff",
                                  color: "var(--oes-blue)",
                                }}
                              >
                                <i className="bi bi-journal-bookmark" />
                              </span>

                              <span
                                className="fw-semibold"
                                style={{
                                  color: "var(--oes-navy)",
                                }}
                              >
                                {purchase.course_name ||
                                  "—"}
                              </span>
                            </div>
                          </td>

                          {/* Exam */}

                          <td>
                            <span
                              className="text-muted"
                            >
                              {purchase.exam_name ||
                                "—"}
                            </span>
                          </td>

                          {/* Exam Date */}

                          <td className="text-muted">
                            {formatExamDate(
                              purchase.exam_date
                            )}
                          </td>

                          {/* Payment Status */}

                          <td>
                            <span
                              className={`small fw-semibold ${getStatusClass(
                                purchase.status
                              )}`}
                            >
                              {getStatusLabel(
                                purchase.status
                              )}
                            </span>
                          </td>

                          {/* Amount */}

                          <td
                            className="text-end fw-semibold"
                            style={{
                              color: "var(--oes-navy)",
                            }}
                          >
                            ₹
                            {formatAmount(
                              purchase.amount
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}